/**
 * coordPill.ts — Phase 254 D-05/D-07/D-08/D-09: the ONE place coordinate-pill DOM is built.
 *
 * Both `src/js/mc/feed.ts` (this plan) and `src/js/mc/heatmap.ts` (plan 254-07) consume this
 * module's `buildCoordPill()`/`appendNarrativeSegments()` rather than each independently building
 * `.mc-coord-pill` markup. The reason it is shared, rather than duplicated once per consumer, is
 * the exact drift scar `src/js/utils/mcKeys.ts`'s own docblock records (Phase 241 D-29/D-30): two
 * byte-identical copies of one function, maintained independently, is how this codebase's real
 * prod regressions have happened before. There is exactly one copy of pill-markup logic here.
 *
 * Imports exactly one thing on purpose: `dimensionPillClass` from `../utils/mcKeys`, the tested
 * mirror-pair resolver from plan 254-02. This module never restates or derives a variant class
 * name from a raw dimension string — that would reopen the exact class of bug `dimensionPillClass`
 * exists to close off (T-254-02).
 *
 * Zero HTML-string-assignment DOM APIs anywhere in this file (the ones this codebase's grep-based
 * checks forbid, per T-231-07/T-232-03) — every node is built via `createElement`/`textContent`/
 * text nodes, matching `src/js/mc/feed.ts`'s own file-header rule, since this module's output lands
 * directly inside that file's rows.
 *
 * Two rendering rules exist, per 254-UI-SPEC.md §9.1 — deliberately NOT collapsible into one
 * branch, each recorded here so a later edit cannot merge them without deleting a stated reason:
 *
 * Rule A (`mode: 'link'`, feed narrative + player-profile position card): a coordinate segment
 * renders as a real clickable `.mc-coord-pill` anchor ONLY when its `href` resolves non-null.
 * When it does not, the coordinate renders as plain, unstyled text — zero pill chrome, zero
 * dimension color. A fully-rounded pill in this app already means "this is clickable" elsewhere
 * (`.mc-feed-arrival-pill`'s own established convention); coloring a dead coordinate would create
 * a false affordance on the 9-of-11 fleet servers with no public BlueMap URL, where this would be
 * the COMMON case, not the edge case.
 *
 * Rule B (`mode: 'decorative'`, heatmap tooltips): a coordinate segment ALWAYS renders as a
 * non-interactive `.mc-coord-pill` span, regardless of `href` — only the trailing arrow glyph is
 * conditional. This is safe because the tooltip's parent canvas already carries its own, separately
 * gated clickability signal (`canvas.style.cursor`), so the pill's color never needs to double as
 * that signal.
 *
 * The `--unknown` fallback class (§9.4) is scoped to `unknownFallback: true` callers only (Rule B
 * tooltips) — `dimensionPillClass()` returning `''` on a Rule A surface means NO variant class at
 * all, never `--unknown`, because a modded dimension on a Rule A surface always also carries a null
 * `href` (`BluemapLink::mapIdForDimension()` returns null for any non-vanilla key), so it already
 * falls into the plain-text branch above and never reaches `buildCoordPill` with a variant class at
 * all in practice.
 *
 * Phase 276 (D-01/D-04/D-11, QNAME-01/02/04) adds a THIRD segment variant, `'quest'` — the
 * structured wire shape `App\Models\McFeedModel::questObjectSegment()` (plan 276-05) emits for
 * `quest.started`/`quest.completed` rows. Its strings (`value`/`task_text`/`object_text`) are
 * wire- and pack-sourced, attacker-adjacent data, exactly like every other value this file writes
 * via `textContent` — never an HTML string. The `mc-quest-unresolved-marker`/
 * `mc-quest-detail-trigger` markup this branch builds mirrors `quests.latte`'s own trigger markup
 * (plan 276-06) attribute-for-attribute, so the Activity Feed and the Quests board render the
 * identical cue for the identical id. A browser tab holding a pre-276 bundle hits this same file's
 * pre-existing "unrecognised segment type" skip below and renders the verb without the object
 * until it reloads — the same already-accepted transient degradation
 * `App\Models\McFeedModel`'s own `narrative` field docblock describes for a deploy window.
 */

// Explicit `.ts` extensions (`allowImportingTsExtensions` in tsconfig.json) on BOTH imports below
// — required, not stylistic: `tests/mc/questDetail.test.ts` (plan 276-09) imports this module
// directly via plain `node file.ts` execution (no bundler, no extension-inferring resolver), and
// Node's native ESM loader cannot resolve an extensionless relative specifier. rspack's own
// bundler resolution accepts the explicit extension identically to the implicit form used
// elsewhere in this codebase.
import { dimensionPillClass } from '../utils/mcKeys.ts';
import { QUEST_UNRESOLVED_TITLE } from './questDetail.ts';

export type NarrativeSegment =
    | { type: 'text'; value: string }
    | { type: 'coord'; x: number; y: number; z: number; dimension: string; href: string | null }
    | {
        type: 'quest';
        value: string;
        task_text: string | null;
        task_unresolved: boolean;
        object_text: string;
        object_unresolved: boolean;
        detail_quest_id: string | null;
    };

export interface BuildCoordPillOptions {
    tag: 'a' | 'span';
    x: number;
    y: number;
    z: number;
    dimension: string;
    href: string | null;
    showArrow: boolean;
    /** Default false. When true, an unrecognised dimension gets the tooltip-only neutral fallback
     *  variant class instead of no variant class at all. Scoped to Rule B (tooltip) callers only —
     *  see file docblock and 254-UI-SPEC.md §9.4 for the exact literal class name. */
    unknownFallback?: boolean;
    /** Default false. Prefixes the rendered `y` value alone with a tilde (e.g. `96, ~64, 12`) —
     *  the density-bucket tooltip's marker for a fabricated sea-level default, never a real
     *  recorded position (254-UI-SPEC.md §9.5). Never applied to `x`/`z`, which are real
     *  (if coarsened-by-design) bucket centres. */
    approximateY?: boolean;
}

export interface AppendNarrativeSegmentsOptions {
    mode: 'link' | 'decorative';
    /** Only consulted in `'decorative'` mode — `'link'` mode's arrow visibility is decided per
     *  segment by whether that segment's own `href` resolves (Rule A). */
    showArrow?: boolean;
}

/**
 * Builds one `.mc-coord-pill` element. `tag: 'a'` for Rule A's real anchor, `tag: 'span'` for Rule
 * B's decorative chrome. Never assigns any HTML-string DOM API — see file docblock.
 */
export function buildCoordPill(opts: BuildCoordPillOptions): HTMLElement {
    // Guard: an anchor caller with no usable href is a caller bug (Rule A callers must check href
    // themselves and render plain text instead — see appendNarrativeSegments below). Rather than
    // emit a broken `<a>` with no `href`, degrade to a non-interactive span so the function always
    // returns a usable element.
    const tag = opts.tag === 'a' && !(typeof opts.href === 'string' && opts.href !== '') ? 'span' : opts.tag;

    const el = document.createElement(tag);
    el.classList.add('mc-coord-pill');

    const variantClass = dimensionPillClass(opts.dimension);
    if (variantClass !== '') {
        el.classList.add(variantClass);
    } else if (opts.unknownFallback === true) {
        // Tooltip-only fallback (254-UI-SPEC.md §9.4) — never reached from a Rule A surface in
        // practice, since an unrecognised dimension there always also carries a null href.
        el.classList.add('mc-coord-pill--unknown');
    }
    // else: no variant class at all — a Rule A caller whose dimension didn't resolve, deliberately
    // left un-tinted rather than guessing a color.

    // Coordinate text, no parentheses (D-06) — three numbers joined by ', '. When `approximateY` is
    // true, the y value alone is tilde-prefixed: it is the fabricated sea-level default, while x/z
    // are real (if bucket-coarsened) values. Never tilde x or z.
    const yText = opts.approximateY === true ? `~${opts.y}` : `${opts.y}`;
    const coordText = `${opts.x}, ${yText}, ${opts.z}`;
    el.appendChild(document.createTextNode(coordText));

    if (opts.showArrow) {
        const arrow = document.createElement('span');
        arrow.className = 'mc-coord-pill-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        el.appendChild(arrow);
    }

    if (tag === 'a') {
        const anchor = el as HTMLAnchorElement;
        // Non-null/non-empty guaranteed by the `tag` degrade-guard above.
        anchor.href = opts.href as string;
        anchor.target = '_blank';
        anchor.rel = 'noopener';
        anchor.setAttribute('aria-label', `Open ${coordText} in BlueMap`);
    }
    // tag === 'span': no href/target/rel/aria-label — decorative chrome inside a
    // pointer-events:none tooltip (D-09); the canvas is the real click target.

    return el;
}

/**
 * Appends one resolved-name-or-raw-id half of a `'quest'` segment to `parent` — a plain text node
 * when `unresolved` is false, or a `<code>` id + `.mc-quest-unresolved-marker` span when true.
 * Mirrors `quests.latte`'s `{include questUnresolvedMarker}` markup and
 * `src/js/mc/questDetail.ts`'s own `buildUnresolvedMarker()` shape — same class name, same
 * `title` copy (imported from `./questDetail` as `QUEST_UNRESOLVED_TITLE`, never re-typed as a
 * literal, per this file's own drift-scar discipline). The class name string itself IS repeated
 * here as a literal (mirroring, not importing) — `questDetail.ts` has no exported constant for it,
 * only for the title copy, and this file's own zero-HTML-string-DOM-APIs discipline builds the
 * class via `classList.add()`, never a shared markup string either module could drift from.
 */
function appendQuestHalf(parent: HTMLElement, text: string, unresolved: boolean): void {
    if (!unresolved) {
        parent.appendChild(document.createTextNode(text));
        return;
    }
    const code = document.createElement('code');
    code.textContent = text;
    parent.appendChild(code);
    parent.appendChild(document.createTextNode(' '));
    const marker = document.createElement('span');
    marker.classList.add('mc-quest-unresolved-marker');
    marker.title = QUEST_UNRESOLVED_TITLE;
    marker.textContent = 'unresolved';
    parent.appendChild(marker);
}

/**
 * Renders an ordered `NarrativeSegment[]` (the wire shape plan 254-03 emits) into `parent`, via one
 * shared loop both `feed.ts` (mode: 'link') and `heatmap.ts` (mode: 'decorative') iterate.
 *
 * Defensively skips any element that is not a recognisable segment — during a deploy window this
 * array can arrive from either side of the change (an already-loaded tab polling a freshly-deployed
 * backend, or vice versa). Skips silently; never throws, and never renders `undefined`/`NaN`.
 */
export function appendNarrativeSegments(
    parent: HTMLElement,
    segments: NarrativeSegment[],
    opts: AppendNarrativeSegmentsOptions,
): void {
    for (const seg of segments) {
        if (seg === null || typeof seg !== 'object') continue;

        if (seg.type === 'text') {
            if (typeof seg.value !== 'string') continue;
            parent.appendChild(document.createTextNode(seg.value));
            continue;
        }

        if (seg.type === 'coord') {
            if (!Number.isFinite(seg.x) || !Number.isFinite(seg.y) || !Number.isFinite(seg.z)) continue;

            if (opts.mode === 'link') {
                // Rule A (254-UI-SPEC.md §9.1): pill only when href resolves; otherwise a PLAIN
                // TEXT NODE of the same coordinate, no element, no class. This asymmetry is
                // deliberate — see file docblock — never "simplify" into always rendering a pill.
                if (typeof seg.href === 'string' && seg.href !== '') {
                    parent.appendChild(buildCoordPill({
                        tag: 'a',
                        x: seg.x,
                        y: seg.y,
                        z: seg.z,
                        dimension: seg.dimension,
                        href: seg.href,
                        showArrow: true,
                        unknownFallback: false,
                    }));
                } else {
                    parent.appendChild(document.createTextNode(`${seg.x}, ${seg.y}, ${seg.z}`));
                }
                continue;
            }

            // Rule B (254-UI-SPEC.md §9.1): always a pill, regardless of href — safe because the
            // tooltip's parent canvas carries its own separately-gated clickability signal.
            parent.appendChild(buildCoordPill({
                tag: 'span',
                x: seg.x,
                y: seg.y,
                z: seg.z,
                dimension: seg.dimension,
                href: seg.href,
                showArrow: opts.showArrow === true,
                unknownFallback: true,
            }));
            continue;
        }

        if (seg.type === 'quest') {
            // Defensive field-shape guard (see file docblock's deploy-window note) — any missing
            // or wrong-typed field skips the WHOLE segment silently, never throws, never renders
            // `undefined`. `task_text`/`detail_quest_id` are nullable; every other field is not.
            if (typeof seg.object_text !== 'string' || typeof seg.object_unresolved !== 'boolean') continue;
            if (typeof seg.task_unresolved !== 'boolean') continue;
            if (seg.task_text !== null && typeof seg.task_text !== 'string') continue;
            if (seg.detail_quest_id !== null && typeof seg.detail_quest_id !== 'string') continue;

            // D-04's task-in-quest shape: `"{task}" in {quest}`, built ONLY when `task_text` is
            // non-null — the chapter/quest-level path below has no task half and no " in " glue.
            // When both halves are unresolved this yields two independent markers, deliberately —
            // see 276-UI-SPEC.md's Task-in-Quest Label table: each names a distinct fact, merging
            // them would hide which half is the honest fallback.
            if (seg.task_text !== null) {
                parent.appendChild(document.createTextNode('"'));
                appendQuestHalf(parent, seg.task_text, seg.task_unresolved);
                parent.appendChild(document.createTextNode('"'));
                parent.appendChild(document.createTextNode(' in '));
            }

            // The detail-popover cue/trigger wraps the OBJECT half ONLY — never the task half,
            // never both (D-11, 276-UI-SPEC.md's Trigger & Affordance section). Inert (no
            // wrapper/tabindex/aria-*) when `detail_quest_id` is null — an element announced as
            // having a disclosure that opens nothing is worse than a plain text node.
            if (typeof seg.detail_quest_id === 'string' && seg.detail_quest_id !== '') {
                const trigger = document.createElement('span');
                trigger.classList.add('mc-quest-detail-trigger');
                trigger.tabIndex = 0;
                trigger.setAttribute('aria-haspopup', 'dialog');
                trigger.setAttribute('aria-label', `Quest detail: ${seg.object_text}`);
                trigger.dataset.questDetailId = seg.detail_quest_id;
                appendQuestHalf(trigger, seg.object_text, seg.object_unresolved);
                parent.appendChild(trigger);
            } else {
                appendQuestHalf(parent, seg.object_text, seg.object_unresolved);
            }

            continue;
        }

        // Unrecognised `type` — skip silently rather than render nothing useful or throw.
    }
}
